import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';

/**
 * IngredientSlots
 * Presentational component to render the cartridges table/grid
 * - Pure UI, no data fetching
 * - Accessible and simple for juniors
 */
const IngredientSlots = ({
	cartridgeOptions,
	machineIngredients,
	changeIngredientTo,
	ingredientsOptions,
	onIngredientChange,
	onToggleEnabled,
	onToggleCustomConfig,
}) => {
	return (
		<div className="p-3">
			{/* Desktop/tablet header */}
			<div className="hidden md:flex grid text-sm font-medium text-600 mb-2 border-bottom-1 surface-border">
				<div className="col-1">Cartridge</div>
				<div className="col-4">Ingredients</div>
				<div className="col-4">Current</div>
				<div className="col-1">Custom Config</div>
				<div className="col-1">Available</div>
				<div className="col-1">Enabled</div>
			</div>

			{cartridgeOptions.map((cartridge, index) => {
				const ingredient = machineIngredients[index];
				const changed = changeIngredientTo[index]?.changed;
				return (
					<div
						key={index}
						className={`grid align-items-center py-0 mb-2 border-top-1 surface-border ${
							cartridge.updatedHighlight ? 'bg-green-50' : 'surface-0'
						} border-round-sm px-2 transition-all transition-duration-200 hover:bg-bluegray-50`}
					>
						<div className="col-12 md:col-1 pt-2 pb-1">
							<span className="font-semibold">{cartridge.text}</span>
						</div>
						<div className="col-12 md:col-4">
							<span className="block md:hidden text-500 text-xs mb-1">Ingredient</span>
							<Dropdown
								value={ingredient?.id}
								options={ingredientsOptions}
								onChange={(e) => onIngredientChange(cartridge.value, e.value, ingredient)}
								optionLabel="label"
								optionValue="value"
								filter
								virtualScrollerOptions={{ itemSize: 36 }}
								placeholder={ingredient?.name || 'Select an ingredient'}
								className="w-full"
							/>
						</div>
						<div className="col-12 md:col-4">
							<span className="block md:hidden text-500 text-xs mb-1">Current</span>
							{changed && changeIngredientTo[index]?.from?.flavor && (
								<div className="flex align-items-center gap-2 mb-1">
									<span className="flex align-items-center">
										<img
											className="bg-red-200 border-round-sm p-1"
											src={changeIngredientTo[index].from?.flavor?.icon || ingredient?.flavor?.icon}
											alt={changeIngredientTo[index].from?.flavor?.name || ingredient?.flavor?.name}
											style={{ width: '1.5rem', height: '1.5rem', marginRight: '0.35rem' }}
										/>
										<span className="text-600 text-xs font-medium text-red-400">
											{changeIngredientTo[index].from?.flavor?.name || ingredient?.flavor?.name}
										</span>
									</span>
									<i className="pi pi-arrow-right text-600 text-xs"></i>
									<span className="flex align-items-center">
										<img
											className="bg-green-200 border-round-sm p-1"
											src={changeIngredientTo[index].to?.flavor?.icon || ingredient?.flavor?.icon}
											alt={changeIngredientTo[index].to?.flavor?.name || ingredient?.flavor?.name}
											style={{ width: '1.5rem', height: '1.5rem', marginRight: '0.35rem' }}
										/>
										<span className="text-700 text-xs font-semibold text-green-500">
											{changeIngredientTo[index].to?.flavor?.name || ingredient?.flavor?.name}
										</span>
									</span>
								</div>
							)}

							{/* Current selection (always visible) */}
							{ingredient?.flavor && (
								<span className="flex align-items-center ">
									<img
										className="bg-gray-200 border-round-sm p-1"
										src={ingredient.flavor.icon}
										alt={ingredient.flavor.name}
										style={{ width: '2rem', height: '2rem', marginRight: '0.5rem' }}
									/>
									<span className="text-700 text-sm font-medium">{ingredient.flavor.name}</span>
								</span>
							)}
						</div>
						<div className="col-6 md:col-1">
							<span className="block md:hidden text-500 text-xs mb-1">Custom Config</span>
							<div className="flex align-items-center justify-content-start md:justify-content-center">
								<InputSwitch checked={ingredient?.customConfig} onChange={() => onToggleCustomConfig(index)} />
							</div>
						</div>
						<div className="col-6 md:col-1">
							<span className="block md:hidden text-500 text-xs mb-1">Available</span>
							<div className="flex align-items-center justify-content-start md:justify-content-center">
								<Tag
									value={ingredient?.available ? 'Yes' : 'No'}
									severity={ingredient?.available ? 'success' : 'danger'}
									className={
										ingredient?.available
											? 'bg-green-100 border-round-md border-1 border-green-500 text-green-500 text-xs'
											: 'bg-red-100 border-round-md border-1 border-red-500 text-red-500 text-xs'
									}
								/>
							</div>
						</div>
						<div className="col-6 md:col-1">
							<span className="block md:hidden text-500 text-xs mb-1">Enabled</span>
							<div className="flex align-items-center justify-content-start md:justify-content-center">
								<InputSwitch checked={ingredient?.enabled} onChange={() => onToggleEnabled(index)} />
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default IngredientSlots;
